import React, { useMemo, useRef, useState } from 'react';
import { activateLegalBoardTerm, updateLegalBoardTerm } from './api';
import { CHANGE_TYPE_HELP, CHANGE_TYPE_LABELS, DEFAULT_ROLE_SUGGESTIONS, WIZARD_STEPS } from './constants';
import LegalBoardCompositionEditor, { errorMessages, termMembersToLocal } from './LegalBoardCompositionEditor';
import {
    addYearsIso,
    allowedChangeTypes,
    membersToPayload,
    newRequestId,
    validateCompositionClient,
} from './legalBoardUi';

const CHANGE_TYPE_ORDER = ['total', 'partial', 'reelection', 'initial'];

/**
 * Three short steps to define a new board: what changed, since when, who.
 * Only drafts go through the wizard; the current board is corrected with the
 * flat LegalBoardCorrection form, and Generalitat/Stripe are followed up as
 * steps on the main screen once the board is confirmed.
 */
function LegalBoardWizard({ term, overview, initialStep = 0, userId, canManage, castellersInfo, onReload, onClose, onDiscard }) {
    const isDraft = term.lifecycleStatus === 'draft';
    const editable = canManage && isDraft;
    const roleSuggestions = overview?.roleSuggestions || DEFAULT_ROLE_SUGGESTIONS;

    const [step, setStep] = useState(() => Math.max(0, Math.min(WIZARD_STEPS.length - 1, initialStep || 0)));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    const [changeType, setChangeType] = useState(term.changeType || 'total');
    // Another board is already current: the first registration no longer applies, a re-election does.
    const hasCurrent = Boolean(overview?.current) && overview.current.id !== term.id;
    const changeTypeOptions = CHANGE_TYPE_ORDER.filter((type) => allowedChangeTypes({ hasCurrent }).includes(type) || type === changeType);
    const [dates, setDates] = useState({
        assemblyDate: term.assemblyDate || '',
        mandateStart: term.mandateStart || '',
        mandateEnd: term.mandateEnd || '',
        nextReviewDate: term.nextReviewDate || '',
    });
    const [dateErrors, setDateErrors] = useState([]);

    const [members, setMembers] = useState(() => termMembersToLocal(term.members));
    const [exceptionNote, setExceptionNote] = useState(term.compositionException || '');
    const [serverErrors, setServerErrors] = useState([]);

    const activateRequestId = useRef(null);

    const validation = useMemo(() => validateCompositionClient(members, { exceptionNote }), [members, exceptionNote]);

    const goTo = (index) => {
        setStep(Math.max(0, Math.min(WIZARD_STEPS.length - 1, index)));
        setError(null);
        setNotice(null);
    };

    const run = async (action) => {
        if (busy) return null;
        setBusy(true);
        setError(null);
        try {
            return await action();
        } catch (actionError) {
            setError(actionError.message || 'No s\'ha pogut completar l\'acció.');
            return null;
        } finally {
            setBusy(false);
        }
    };

    // -------------------------------------------------------------------
    // Step 1: change type
    // -------------------------------------------------------------------

    const handleChangeTypeNext = () => run(async () => {
        if (editable && changeType !== term.changeType) {
            await updateLegalBoardTerm({ userId, termId: term.id, payload: { changeType } });
            await onReload();
        }
        setStep(1);
    });

    // -------------------------------------------------------------------
    // Step 2: dates
    // -------------------------------------------------------------------

    const handleSaveDates = () => run(async () => {
        setDateErrors([]);
        try {
            const response = await updateLegalBoardTerm({ userId, termId: term.id, payload: { ...dates } });
            await onReload();
            const result = response?.validation?.dates;
            if (result && !result.valid) {
                setDateErrors(errorMessages(result.errors));
                return;
            }
            setStep(2);
        } catch (saveError) {
            const details = saveError?.payload?.details?.dates?.errors;
            if (details) setDateErrors(errorMessages(details));
            throw saveError;
        }
    });

    // -------------------------------------------------------------------
    // Step 3: composition
    // -------------------------------------------------------------------

    const handleMembersChange = (next) => {
        setMembers(next);
        setServerErrors([]);
    };

    const handleSaveMembers = ({ activate = false } = {}) => run(async () => {
        setServerErrors([]);
        if (activate && !validation.valid) {
            setServerErrors(errorMessages(validation.errors));
            return;
        }
        try {
            const response = await updateLegalBoardTerm({
                userId,
                termId: term.id,
                payload: { members: membersToPayload(members), compositionException: exceptionNote },
            });
            const result = response?.validation;
            if (!activate) {
                await onReload();
                setNotice({ tone: 'info', text: 'Esborrany desat. Pots continuar més tard.' });
                return;
            }
            if (result?.dates && !result.dates.valid) {
                await onReload();
                setServerErrors(['Abans de confirmar, completa les dates del mandat.', ...errorMessages(result.dates.errors)]);
                return;
            }
            if (result?.composition && !result.composition.valid) {
                await onReload();
                setServerErrors(errorMessages(result.composition.errors));
                return;
            }
            if (!activateRequestId.current) activateRequestId.current = newRequestId();
            await activateLegalBoardTerm({ userId, termId: term.id, requestId: activateRequestId.current });
            activateRequestId.current = null;
            await onReload();
            onClose({
                message: { tone: 'success', text: 'Junta confirmada com a vigent. Ara toca comunicar-la a la Generalitat.' },
                openStep: 'gencat',
            });
        } catch (saveError) {
            const details = saveError?.payload?.details;
            const messages = [...errorMessages(details?.dates?.errors), ...errorMessages(details?.composition?.errors)];
            if (messages.length > 0) setServerErrors(messages);
            throw saveError;
        }
    });

    // -------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------

    const renderStepper = () => (
        <ol className="legal-board-stepper" aria-label="Passos">
            {WIZARD_STEPS.map((wizardStep, index) => (
                <li key={wizardStep.key} className={`legal-board-step ${index === step ? 'active' : ''} ${index < step ? 'done' : ''}`}>
                    <button type="button" onClick={() => goTo(index)} disabled={busy} aria-current={index === step ? 'step' : undefined}>
                        <span className="legal-board-step-number" aria-hidden="true">{index < step ? <i className="fas fa-check"></i> : index + 1}</span>
                        <span className="legal-board-step-label">{wizardStep.label}</span>
                    </button>
                </li>
            ))}
        </ol>
    );

    const renderChangeTypeStep = () => (
        <div className="legal-board-step-body">
            <p className="legal-board-muted">Què ha passat a la colla? Recorda que ser junta a Aleta (permisos de l&apos;app) no vol dir ser membre de la junta legal.</p>
            <div className="legal-board-options" role="radiogroup" aria-label="Tipus de canvi">
                {changeTypeOptions.map((type) => (
                    <button
                        key={type}
                        type="button"
                        role="radio"
                        aria-checked={changeType === type}
                        className={`legal-board-option ${changeType === type ? 'selected' : ''}`}
                        onClick={() => (editable ? setChangeType(type) : null)}
                        disabled={!editable || busy}
                    >
                        <span className="legal-board-option-title">{CHANGE_TYPE_LABELS[type]}</span>
                        <span className="legal-board-option-description">{CHANGE_TYPE_HELP[type]}</span>
                    </button>
                ))}
            </div>
            <div className="legal-board-actions">
                <button type="button" className="legal-board-button primary" onClick={editable ? handleChangeTypeNext : () => goTo(1)} disabled={busy}>Següent</button>
            </div>
        </div>
    );

    const renderDatesStep = () => (
        <div className="legal-board-step-body">
            <div className="legal-board-form-grid">
                <div className="legal-board-field">
                    <label htmlFor="legal-board-assembly-date">Data de l&apos;assemblea{changeType === 'initial' ? ' (opcional)' : ''}</label>
                    <input id="legal-board-assembly-date" type="date" value={dates.assemblyDate} onChange={(event) => setDates((value) => ({ ...value, assemblyDate: event.target.value }))} disabled={!editable || busy} />
                </div>
                <div className="legal-board-field">
                    <label htmlFor="legal-board-mandate-start">Inici del mandat</label>
                    <input id="legal-board-mandate-start" type="date" value={dates.mandateStart} onChange={(event) => setDates((value) => ({ ...value, mandateStart: event.target.value }))} disabled={!editable || busy} required />
                </div>
                <div className="legal-board-field">
                    <label htmlFor="legal-board-mandate-end">Final previst (opcional)</label>
                    <input id="legal-board-mandate-end" type="date" value={dates.mandateEnd} onChange={(event) => setDates((value) => ({ ...value, mandateEnd: event.target.value }))} disabled={!editable || busy} />
                </div>
                <div className="legal-board-field">
                    <label htmlFor="legal-board-review-date">Pròxima revisió</label>
                    <input id="legal-board-review-date" type="date" value={dates.nextReviewDate} onChange={(event) => setDates((value) => ({ ...value, nextReviewDate: event.target.value }))} disabled={!editable || busy} required />
                    {editable && dates.mandateStart ? (
                        <div className="legal-board-chips">
                            {[1, 2, 4].map((years) => (
                                <button key={years} type="button" className="legal-board-chip" onClick={() => setDates((value) => ({ ...value, nextReviewDate: addYearsIso(value.mandateStart, years) }))} disabled={busy}>
                                    {years === 1 ? '1 any després' : `${years} anys després`}
                                </button>
                            ))}
                        </div>
                    ) : null}
                    <small>Aleta avisarà les persones amb permís per gestionar la junta quan faltin 45 i 15 dies, i si la data passa. L’avís apareixerà a les notificacions de l’app; també arribarà per push si les tenen activades.</small>
                </div>
            </div>
            {dateErrors.length > 0 ? <p className="legal-board-field-error" role="alert">{dateErrors.join(' · ')}</p> : null}
            <div className="legal-board-actions">
                <button type="button" className="legal-board-button primary" onClick={editable ? handleSaveDates : () => goTo(2)} disabled={busy}>
                    {editable ? 'Desar i continuar' : 'Següent'}
                </button>
                <button type="button" className="legal-board-button link" onClick={() => goTo(0)} disabled={busy}>Enrere</button>
            </div>
        </div>
    );

    const renderCompositionStep = () => (
        <div className="legal-board-step-body">
            <p className="legal-board-muted">
                Només les persones que consten (o constaran) al Registre. Es guarda el nom tal com el poses ara, així l&apos;historial no canvia.
            </p>
            <LegalBoardCompositionEditor
                members={members}
                onMembersChange={handleMembersChange}
                exceptionNote={exceptionNote}
                onExceptionChange={setExceptionNote}
                castellersInfo={castellersInfo}
                roleSuggestions={roleSuggestions}
                validation={validation}
                serverErrors={serverErrors}
                disabled={!editable}
                busy={busy}
            />
            <div className="legal-board-actions">
                {editable ? (
                    <>
                        <button type="button" className="legal-board-button primary" onClick={() => handleSaveMembers({ activate: true })} disabled={busy}>Confirmar la junta</button>
                        <button type="button" className="legal-board-button link" onClick={() => handleSaveMembers({ activate: false })} disabled={busy}>Desar l&apos;esborrany</button>
                    </>
                ) : null}
                <button type="button" className="legal-board-button link" onClick={() => goTo(1)} disabled={busy}>Enrere</button>
            </div>
        </div>
    );

    const renderStep = () => {
        if (step === 0) return renderChangeTypeStep();
        if (step === 1) return renderDatesStep();
        return renderCompositionStep();
    };

    return (
        <section className="legal-board-card legal-board-wizard" aria-labelledby="legal-board-wizard-title">
            <header className="legal-board-card-head">
                <div>
                    <span className="legal-board-eyebrow">Esborrany · {CHANGE_TYPE_LABELS[term.changeType] || term.changeType}</span>
                    <h3 id="legal-board-wizard-title">{WIZARD_STEPS[step].label}</h3>
                </div>
                <div className="legal-board-wizard-head-actions">
                    {editable && typeof onDiscard === 'function' ? (
                        <button type="button" className="legal-board-button link" onClick={onDiscard} disabled={busy}>Descartar l&apos;esborrany</button>
                    ) : null}
                    <button type="button" className="legal-board-button link" onClick={() => onClose()} disabled={busy}>Tancar</button>
                </div>
            </header>
            {renderStepper()}
            {notice ? (
                <div className={`legal-board-alert ${notice.tone || 'info'}`} role="status">
                    <span>{notice.text}</span>
                    <button type="button" className="legal-board-alert-close" onClick={() => setNotice(null)} aria-label="Tancar l'avís">×</button>
                </div>
            ) : null}
            {error ? <div className="legal-board-alert error" role="alert">{error}</div> : null}
            {renderStep()}
        </section>
    );
}

export default LegalBoardWizard;
